import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { NavigationService } from '../../../services/navigation.service';

import { RootState, MultiSelect, CalendarIcon, Skill, EquipmentIcon } from '@umanforge/common';

import { ROUTES } from '../../../constants/navigation.constants';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';

import { HomeTopBar } from '../../../components/menu/home_top_bar.component';
import { Breadcrumb } from '../../../components/menu/breadcrumb.component';
import { Button } from '../../../components/inputs/button.component';
import { Input } from '../../../components/inputs/input.component';
import { Select } from '../../../components/inputs/select.component';
import { Loading } from '../../../components/loading.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { ClientsIcon } from '../../../components/icons/clients_icon.component';
import { SitesIcon } from '../../../components/icons/sites_icon.component';
import { AbilitiesIcon } from '../../../components/icons/abilities_icon.component';
import { SaveIcon } from '../../../components/icons/save_icon.component';
import { PlusIcon } from '../../../components/icons/plus_icon.component';
import { useCreateEquipment } from '../hooks/create_equipment.hook';
import { SkillsIcon } from 'components/icons/skills_icon.component';
import { ILUOSkill } from 'components/users/iluo_skill.component';
import { ExpirationDate } from '../components/expiration_date.component';
import { SectorsIcon } from 'components/icons/sectors_icon.component';


export function CreateEquipmentView() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );

  const {
    postEquipment, setPostEquipment,
    postSector, setPostSector,
    postSite, setPostSite,
    postClient, setPostClient,
    selectedClient, setSelectedClient,
    selectedSite, setSelectedSite,
    selectedSector, setSelectedSector,
    allowedClients,
    allowedSites,
    allowedSectors,
    rerenderClient,
    rerenderSite,
    rerenderSector,
    canPost,
    hasChange,
    needCreateNewSite,
    needCreateNewSector,
    needCreateNewClient,
    isLoading,
    onCreateClick,
    createNewSite,

    selectedAbilities, setSelectedAbilities,
    getAbilities,

    selectedSkills, setSelectedSkills,
    getSkills
  } = useCreateEquipment();

  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ hasChange } />
      <div className='flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'create_equipment' ) } needConfirmationToNav={ hasChange }/>
        <Breadcrumb
          needConfirmationToNav={ hasChange }
          links={
            [
              { label: t( 'common:equipment' ), route: ROUTES.EQUIPMENTS, icon: ( <EquipmentIcon className='h-[14px] select-none cursor-pointer'/> ) },
              { label: t( 'create_new_equipment' ), route: ROUTES.NEW_EQUIPMENT, icon: ( <PlusIcon className='h-[12px] select-none cursor-pointer'/> ) }
            ]
          }
        />
        {
          !isLoading ?
            <div className='flex flex-col items-center justify-center'>
              <form className='relative flex flex-col gap-4 h-full w-full mb-12 items-center justify-center p-4 px-[10%]'>

                <div className='flex flex-col w-full'>
                  <Select
                    required
                    key={ rerenderClient }
                    disabled={ allowedClients.length === 1 }
                    value={ selectedClient }
                    label={ t( 'Client' ) }
                    placeholder={ t( 'No associated clients' ) }
                    icon={ <ClientsIcon className='w-7 h-7' /> }
                    options={ allowedClients }
                    onChange={ option => {
                      setSelectedClient( option );
                    } }
                  />
                  <div className='flex flex-col w-full'>
                    <div className={`flex mx-3 border-x-2 border-b-2 rounded-b-md origin-top transition-all duration-500 ${needCreateNewClient ? 'mt-[2px] p-2 scale-100 opacity-100 max-h-96' : 'scale-y-50 opacity-0 max-h-0'}`}>
                      <Input
                        required
                        maxLength={ MAX_LENGTH_ENTITY.UF_CLIENT_NAME }
                        label={ t( 'Client name' ) }
                        placeholder={ t( 'Client' ) + '...' }
                        icon={ <ClientsIcon className='w-7 h-7' /> }
                        value={ postClient.name }
                        onChange={ newClientName => {
                          postClient.name = newClientName;
                          setPostClient( structuredClone( postClient ) );
                        } }
                      />
                    </div>
                  </div>
                </div>

                <div className='flex flex-col w-full'>
                  <Select
                    required
                    key={ rerenderSite }
                    disabled={ !selectedClient || allowedSites.length === 1 }
                    value={ selectedSite }
                    label={ t( 'Site' ) }
                    placeholder={ t( 'No associated sites' ) }
                    icon={ <SitesIcon className='w-7 h-7' /> }
                    options={ allowedSites }
                    onChange={ option => {
                      setSelectedSite( option );
                    } }
                  />
                  <div className='w-full'>
                    <div className={`flex mx-3 border-x-2 border-b-2 rounded-b-md origin-top transition-all duration-500 ${needCreateNewSite ? 'mt-[2px] p-2 scale-100 opacity-100 max-h-96' : 'scale-y-50 opacity-0 max-h-0'}`}>
                      <Input
                        required
                        maxLength={ MAX_LENGTH_ENTITY.UF_SITE_NAME }
                        label={ t( 'Site name' ) }
                        placeholder={ t( 'Site' ) + '...' }
                        icon={ <SitesIcon className='w-7 h-7' /> }
                        value={ postSite.name }
                        onChange={ newSiteName => {
                          postSite.name = newSiteName;
                          setPostSite( structuredClone( postSite ) );
                        } }
                      />
                    </div>
                  </div>
                </div>

                <div className='flex flex-col w-full'>
                  <Select
                    required
                    key={ rerenderSector }
                    disabled={ !selectedClient || allowedSectors.length === 1 }
                    value={ selectedSector }
                    label={ t( 'Sector' ) }
                    placeholder={ t( 'No associated sectors' ) }
                    icon={ <SectorsIcon className='w-7 h-7' /> }
                    options={ allowedSectors }
                    onChange={ option => {
                      setSelectedSector( option );
                    } }
                  />
                  <div className='w-full'>
                    <div className={`flex mx-3 border-x-2 border-b-2 rounded-b-md origin-top transition-all duration-500 ${needCreateNewSector ? 'mt-[2px] p-2 scale-100 opacity-100 max-h-96' : 'scale-y-50 opacity-0 max-h-0'}`}>
                      <Input
                        required
                        maxLength={ MAX_LENGTH_ENTITY.UF_SECTOR_NAME }
                        label={ t( 'Sector name' ) }
                        placeholder={ t( 'Sector' ) + '...' }
                        icon={ <SectorsIcon className='w-7 h-7' /> }
                        value={ postSector.name }
                        onChange={ newSectorName => {
                          postSector.name = newSectorName;
                          setPostSector( structuredClone( postSector ) );
                        } }
                      />
                    </div>
                  </div>
                </div>

                <div className='w-full'>
                  <Input
                    required
                    maxLength={ MAX_LENGTH_ENTITY.UF_EQUIPMENT_NAME }
                    label={ t( 'equipment_name' ) }
                    disabled={ false }
                    placeholder={ t( 'common:equipment' ) + '...' }
                    icon={ <EquipmentIcon className='w-7 h-7' /> }
                    value={ postEquipment.name ?? ''}
                    onChange={ newEquipmentName => {
                      postEquipment.name = newEquipmentName;
                      setPostEquipment( structuredClone( postEquipment ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    required
                    maxLength={ MAX_LENGTH_ENTITY.UF_EQUIPMENT_DESCRIPTION }
                    label={ t( 'equipment_description' ) }
                    disabled={ false }
                    placeholder={ t( 'equipment_description' ) + '...' }
                    icon={ <EquipmentIcon className='w-7 h-7' /> }
                    value={ postEquipment.description ?? ''}
                    onChange={ newEquipmentDescription => {
                      postEquipment.description = newEquipmentDescription;
                      setPostEquipment( structuredClone( postEquipment ) );
                    } }
                  />
                </div>

                <div className='w-1/4 min-w-[225px] self-start'>
                  <ExpirationDate
                    required
                    label={ t( 'expiration_date' ) }
                    disabled={ false }
                    icon={ <CalendarIcon className='w-7 h-7' /> }
                    equipment={postEquipment}
                    onChange={ newValidityDate => {
                      postEquipment.expirationDate = newValidityDate;
                      setPostEquipment( structuredClone( postEquipment ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <MultiSelect
                    label={ t( 'common:authorization' ) + '(' + selectedAbilities.length + ')' }
                    disabled={ !selectedSite || selectedSite.object.id === createNewSite.id }
                    placeholder={ t( 'No associated authorizations' ) }
                    icon={ <AbilitiesIcon className='w-7 h-7' /> }
                    clearable={ false }
                    selectedValues={ selectedAbilities }
                    getValues={ getAbilities }
                    onChange={ options => {
                      setSelectedAbilities( options );
                    } }
                    extra={ undefined }
                  />
                </div>
                <div className='w-full'>
                  <MultiSelect
                    label={ t( 'common:skill' ) + '(' + selectedSkills.length + ')' }
                    disabled={ !selectedSite || selectedSite.object.id === createNewSite.id }
                    placeholder={ t( 'No associated skills' ) }
                    icon={ <SkillsIcon className='w-7 h-7' /> }
                    clearable={ false }
                    selectedValues={ selectedSkills }
                    getValues={ getSkills }
                    onChange={ options => {
                      setSelectedSkills( options );
                    } }
                    extra={
                      skill => {
                        return (
                          <ILUOSkill
                            skill={ ( skill.object as unknown as Skill ) }
                            onChange={ changedSkill => {
                              const findedSkill = selectedSkills.find( item => { return changedSkill.id == item.object.id;  } );
                              if ( findedSkill ) {
                                findedSkill.object.value = changedSkill.value;
                              }
                              setSelectedSkills( structuredClone( selectedSkills ) );
                            } }
                          />
                        );
                      }
                    }
                  />
                </div>

                <div className='flex h-fit w-1/3 min-w-[160px] max-w-[250px] justify-center mt-10'>
                  <Button
                    label={ t( 'Create' ) }
                    disable={ !canPost }
                    icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                    color='#0bb173'
                    colorHover='#088D5C'
                    onClick={ onCreateClick }
                  />
                </div>
              </form>
            </div>
            :
            <Loading/>
        }
      </div>
    </>
  );

}